import { KeyboardEvent, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { PlusIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { createOrganization } from '@/services/admin/create-organization';
import { Organization } from '@/types';
import { useNotification } from '@/components/notifications';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';

interface CreateOrgResponse {
  orgid: string;
  name: string;
}

interface CreateOrganizationModalProps {
  updateOrganizationList: (org: Organization) => void;
  isOpen?: boolean;
  setCreateOrgModalOpen: (isModalOpen: boolean) => void;
}

export const CreateOrganizationModal = ({
  setCreateOrgModalOpen,
  updateOrganizationList,
  isOpen = false,
}: CreateOrganizationModalProps) => {
  const notification = useNotification();
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleCreateOrg = async (name: string) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return;
    }

    if (trimmedName.length > 120) {
      setHasError(true);
      return;
    }

    setIsLoading(true);

    try {
      const result = await createOrganization<CreateOrgResponse>(name);

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      updateOrganizationList({
        id: result?.data?.orgid || '',
        name: trimmedName,
        imageUrl: '',
      });

      notification.success('Organization was created successfully');

      setCreateOrgModalOpen(false);
    } catch (error) {
      console.error('Create organization error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!name?.trim()) {
      return;
    }

    if (event.key === 'Enter') {
      handleCreateOrg(name);
    }
  };

  const Title = () => (
    <div className='flex items-center gap-2'>
      <PlusIcon className='h-5 w-5' />
      Add organization
    </div>
  );

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button variant='outline'>Cancel</Button>
      </DialogClose>
      <Button
        disabled={!name?.trim() || isLoading}
        onClick={() => handleCreateOrg(name)}
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Saving...
          </>
        ) : (
          'Save'
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(isOpen) => {
        setName('');
        setHasError(false);
        setCreateOrgModalOpen(isOpen);
      }}
      title={<Title />}
      footer={<Footer />}
    >
      <div className='mt-4'>
        <Input
          error={
            hasError ? 'Organization name cannot exceed 120 characters' : ''
          }
          onChange={(event) => {
            setHasError(false);
            setName(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          value={name}
        />
      </div>
    </Modal>
  );
};
